<script setup lang="ts">
import { ParagraphShopTeasers } from '~/api/graphql/generated'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  entity: ParagraphShopTeasers
}>()

const link = computed(() => ({
  url: props.entity.fieldLink?.url?.path ?? '',
  text: props.entity.fieldLink?.title ?? '',
  target: props.entity.fieldLink?.options.attributes?.target ?? '',
}))

const ids = computed(
  () =>
    props.entity.fieldProduct?.map((product) => {
      const matches = product?.match(/.*\((?<productId>[0-9a-z]{32})\)/) as RegExpMatchArray | null

      if (matches !== null && matches.groups) {
        return matches.groups.productId
      }

      return ''
    }) || [],
)

const { search } = useProductSearch()

const { data, error } = await useAsyncData(`paragraph shop teasers ${props.entity.id}`, async () => {
  try {
    const productPromises = ids.value.map(async (id) => {
      if (!id) {
        return { status: 'rejected', reason: 'Invalid product ID' }
      }

      try {
        const response = await search(id, {
          withCmsAssociations: false,
          criteria: {
            associations: {
              properties: {},
              seoUrls: {},
            },
          },
        })

        if (!response?.product) {
          return { status: 'rejected', reason: 'Product not found' }
        }

        return { status: 'fulfilled', value: response.product }
      } catch (err) {
        console.warn(`Failed to fetch product ${id}:`, err)
        return {
          status: 'rejected',
          reason: `Failed to fetch product: ${err instanceof Error ? err.message : 'Unknown error'}`,
        }
      }
    })

    const results = await Promise.allSettled(productPromises)

    return results.map((result) => {
      if (result.status === 'fulfilled') {
        const productResult = result.value
        if (productResult.status === 'fulfilled') {
          // Serialize the product to remove non-serializable properties
          return JSON.parse(JSON.stringify(productResult))
        }
        return productResult
      }
      return {
        status: 'rejected',
        reason: result.reason instanceof Error ? result.reason.message : String(result.reason),
      }
    })
  } catch (err) {
    console.error('Error fetching shop teasers:', err)
    // Return empty array instead of throwing, as this gets serialized
    return []
  }
})

const products = computed(() => {
  if (!data.value) return []

  return data.value.filter((res) => res.status === 'fulfilled' && res.value).map((res: any) => res.value)
})

const hasErrors = computed(() => {
  if (!data.value) return false
  return data.value.some((res) => res.status === 'rejected')
})

const errorCount = computed(() => {
  if (!data.value) return 0
  return data.value.filter((res) => res.status === 'rejected').length
})

// Log errors in development
if (process.dev && hasErrors.value) {
  console.warn(`Shop teasers: ${errorCount.value} out of ${ids.value.length} products failed to load`)
}
</script>

<template>
  <Section v-animate="{ animation: 'vertical-reveal' }">
    <SectionTitle
      :title="entity.fieldTitle ?? undefined"
      :sub-title="entity.fieldDescription?.processed ?? undefined"
    />

    <!-- Show products if available -->
    <Products v-if="products.length" :products="products" />

    <!-- Show message if no products loaded but some failed -->
    <div v-else-if="hasErrors && !products.length" class="error-message">
      <p>{{ t('shop.shopTeasers.errorMessage') }}</p>
    </div>

    <!-- Show message if no products configured -->
    <div v-else-if="!ids.length" class="no-products-message">
      <p>{{ t('shop.shopTeasers.noProductsMessage') }}</p>
    </div>

    <!-- Show button if link is available -->
    <Button v-if="link?.url" secondary icon="offers" :to="link.url">{{ link.text }}</Button>
  </Section>
</template>
<style lang="scss" scoped>
.button.secondary {
  @include fluid(margin-top, 48px, 64px);
  @include breakpoint(ts) {
    align-self: end;
    justify-self: start;
  }
}
</style>
